import { Box, styled } from "@mui/material";
import { CONDUCCO_PRIMARY, INSIGHTS_BORDER, INSIGHTS_FONT } from "./tokens";

export const MessageContainer = styled(Box)<{ isUser?: boolean }>(({ theme, isUser }) => ({
  display: 'flex',
  justifyContent: isUser ? 'flex-end' : 'flex-start',
  marginBottom: theme.spacing(3),
  animation: 'insightsFadeIn 0.35s ease-out',
  '@keyframes insightsFadeIn': {
    from: { opacity: 0, transform: 'translateY(12px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}));

export const MessageBubble = styled(Box)<{ isUser?: boolean }>(({ theme, isUser }) => ({
  maxWidth: '85%',
  padding: theme.spacing(2, 2.5),
  borderRadius: theme.spacing(2),
  fontFamily: INSIGHTS_FONT,
  backgroundColor: isUser ? CONDUCCO_PRIMARY : 'transparent',
  color: isUser ? '#FFFFFF' : theme.palette.text.primary,
  border: 'none',
  wordWrap: 'break-word',
  boxShadow: isUser ? '0 4px 14px rgba(48,36,225,0.22)' : 'none',
}));

// A "câmera" do chat: sem fundo, sem borda, sem sombra — a página inteira é o chat.
export const ChatContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: 1,
  minHeight: 0,
  width: '100%',
  backgroundColor: '#FFFFFF',
}));

export const MessagesArea = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3, 2),
  backgroundColor: '#FFFFFF',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[300],
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.grey[400],
  },
}));

// Coluna central que dá o alinhamento comum entre mensagens e o dock de input.
export const CenteredColumn = styled(Box)(() => ({
  width: '100%',
  maxWidth: 760,
}));

export const InputDock = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2, 2, 3),
  backgroundColor: '#FFFFFF',
}));

export const InputArea = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'flex-end',
}));

// O anel azul permanente fazia o campo gritar mesmo sem foco, e era a única
// coisa realçada na tela inteira. Neutro em repouso, azul quando está sendo
// usado — o mesmo comportamento dos campos dos cards de relatório.
export const InputCard = styled(Box)(({ theme }) => ({
  width: '100%',
  border: `1px solid ${INSIGHTS_BORDER}`,
  borderRadius: '16px',
  padding: theme.spacing(1.5, 1.5, 1),
  backgroundColor: '#FFFFFF',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    borderColor: '#C4C4D4',
  },
  '&:focus-within': {
    borderColor: CONDUCCO_PRIMARY,
    boxShadow: '0 0 0 3px rgba(48,36,225,0.14)',
  },
}));

export const InputToolbar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(0.5),
}));

export const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  width: '100%',
  minHeight: '50vh',
  padding: theme.spacing(4, 2),
  textAlign: 'center',
}));

export const SuggestionsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: theme.spacing(1.25),
  marginTop: theme.spacing(3),
  width: '100%',
}));

// Mesma linguagem do card de relatório: raio de 14px, borda que escurece no
// hover e sombra fria em vez do azul. Antes o atalho hover ficava azul e o card
// de relatório cinza — a mesma interação com duas gramáticas.
export const SuggestionCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.25, 1.5),
  backgroundColor: '#fff',
  borderRadius: '14px',
  cursor: 'pointer',
  fontFamily: INSIGHTS_FONT,
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
  border: `1px solid ${INSIGHTS_BORDER}`,
  '&:hover': {
    borderColor: '#CFCFDD',
    boxShadow: '0 6px 18px rgba(26,26,46,0.08)',
  },
  '&:focus-visible': {
    outline: `2px solid ${CONDUCCO_PRIMARY}`,
    outlineOffset: 2,
  },
}));

export const SuggestionIconBadge = styled(Box)(() => ({
  display: 'grid',
  placeItems: 'center',
  width: 30,
  height: 30,
  flexShrink: 0,
  borderRadius: '9px',
  backgroundColor: 'rgba(48,36,225,0.10)',
}));

export const QuickQuestionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  width: '100%',
  paddingBottom: theme.spacing(1.5),
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  '&::-webkit-scrollbar': {
    height: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.grey[300],
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.grey[400],
  },
}));
